import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as DeniedIcon,
  Redeem as FulfilledIcon,
  LocalOffer as PriceIcon
} from '@mui/icons-material';
import { PrizeRedemption, Prize } from '../../types';
import { prizeService } from '../../services/prizeService';
import { useAuth } from '../../context/AuthContext';

interface MyRedemptionsTabProps {
  onRedemptionUpdated: () => void;
}

const MyRedemptionsTab: React.FC<MyRedemptionsTabProps> = ({
  onRedemptionUpdated
}) => {
  const { user } = useAuth();
  const [redemptions, setRedemptions] = useState<PrizeRedemption[]>([]);
  const [prizes, setPrizes] = useState<{ [key: string]: Prize }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      loadRedemptions();
    }
  }, [user]);

  const loadRedemptions = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const redemptionsData = await prizeService.getRedemptionsByUser(user.id);
      setRedemptions(redemptionsData);

      // Load prize details for each redemption
      const prizePromises = redemptionsData.map(redemption =>
        prizeService.getPrizeById(redemption.prizeId)
      );
      const prizeResults = await Promise.all(prizePromises);
      
      const prizeMap: { [key: string]: Prize } = {};
      prizeResults.forEach((prize, index) => {
        if (prize) {
          prizeMap[redemptionsData[index].prizeId] = prize;
        }
      });
      setPrizes(prizeMap);
    } catch (err: any) {
      console.error('Error loading redemptions:', err);
      setError(err.message || 'Failed to load redemptions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'denied': return 'error';
      case 'fulfilled': return 'info';
      default: return 'default';
    }
  };

  const getStatusDescription = (status: string) => {
    switch (status) {
      case 'pending': return 'Waiting for coach approval';
      case 'approved': return 'Approved! Your coach will fulfill this soon';
      case 'denied': return 'Request was denied by your coach';
      case 'fulfilled': return 'Prize has been fulfilled! Enjoy!';
      default: return 'Unknown status';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <PendingIcon />;
      case 'approved': return <ApprovedIcon />;
      case 'denied': return <DeniedIcon />;
      case 'fulfilled': return <FulfilledIcon />;
      default: return <PendingIcon />;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate ? timestamp.toDate().toLocaleDateString() : new Date(timestamp).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (redemptions.length === 0) {
    return (
      <Alert severity="info">
        You haven't redeemed any prizes yet. Check out the available prizes and start earning points!
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        My Prize Redemptions ({redemptions.length})
      </Typography>

      <List>
        {redemptions.map((redemption, index) => {
          const prize = prizes[redemption.prizeId];
          
          return (
            <React.Fragment key={redemption.id}>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">
                        {prize?.name || 'Unknown Prize'}
                      </Typography>
                      <Chip
                        icon={getStatusIcon(redemption.status)}
                        label={redemption.status.charAt(0).toUpperCase() + redemption.status.slice(1)}
                        color={getStatusColor(redemption.status) as any}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {prize?.description || 'Prize details not available'}
                      </Typography>
                      
                      {/* Status Description */}
                      <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                        {getStatusDescription(redemption.status)}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PriceIcon fontSize="small" color="primary" />
                          <Typography variant="body2">
                            {redemption.pointsSpent} points
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                          Requested: {formatDate(redemption.requestedAt)}
                        </Typography>
                      </Box>

                      {redemption.approvedAt && (
                        <Typography variant="body2" color="text.secondary">
                          {redemption.status === 'approved' ? 'Approved' : 'Denied'}: {formatDate(redemption.approvedAt)}
                        </Typography>
                      )}

                      {redemption.fulfilledAt && (
                        <Typography variant="body2" color="text.secondary">
                          Fulfilled: {formatDate(redemption.fulfilledAt)}
                        </Typography>
                      )}

                      {redemption.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                          <strong>Notes:</strong> {redemption.notes}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              {index < redemptions.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default MyRedemptionsTab;
